import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import NavigationBar from 'components/Navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { facilityService, FacilityDetail } from 'services/facilityService';
import {
  Body,
  Header,
  BackButton,
  FacilityInfo,
  FacilityImage,
  FacilityDetails,
  FacilityName,
  FacilityLocation,
  CalendarContainer,
  WeekHeader,
  TimeColumn,
  DayHeader,
  TimeGrid,
  TimeSlot,
  DayColumn,
  DayCell,
  EventBlock
} from './Status.styles';

interface ReservationEvent {
  dayIndex: number;
  startHour: number;
  endHour: number;
  title: string;
}

const Status: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [facilityData, setFacilityData] = useState<FacilityDetail | null>(null);
  const [currentWeek, setCurrentWeek] = useState<Date[]>([]);
  const [events, setEvents] = useState<ReservationEvent[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const timeSlots = Array.from({ length: 9 }, (_, i) => i + 9); // 9AM to 5PM

  const handleGoBack = () => {
    navigate(`/facilities/${id}`, { replace: true });
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setIsLoading(false);
        return;
      }

      try {
        // Fetch facility details
        const facility = await facilityService.getFacilityDetail(Number(id));
        setFacilityData(facility);

        // Get current week starting from today
        const today = new Date();
        const week = Array.from({ length: 7 }, (_, i) => {
          const date = new Date(today);
          date.setDate(today.getDate() + i);
          return date;
        });
        setCurrentWeek(week);

        // Format today's date for API call
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;

        // Fetch weekly schedule
        const weeklySchedule = await facilityService.getWeeklySchedule(Number(id), dateString);

        // Convert weekly schedule to events
        const bookedEvents: ReservationEvent[] = [];
        weeklySchedule.forEach((dailySchedule, dayIndex) => {
          const bookedSlots = dailySchedule.timeSlots.filter(slot => slot.isBooked);

          // Group consecutive booked slots
          let currentEvent: ReservationEvent | null = null;
          bookedSlots.forEach((slot) => {
            const slotTime = new Date(slot.startTime);
            const slotHour = slotTime.getHours();

            if (!currentEvent) {
              currentEvent = {
                dayIndex,
                startHour: slotHour,
                endHour: slotHour + (slot.durationMinutes / 60),
                title: '예약됨'
              };
            } else if (currentEvent.endHour === slotHour) {
              // Extend current event
              currentEvent.endHour = slotHour + (slot.durationMinutes / 60);
            } else {
              // Save current event and start new one
              bookedEvents.push(currentEvent);
              currentEvent = {
                dayIndex,
                startHour: slotHour,
                endHour: slotHour + (slot.durationMinutes / 60),
                title: '예약됨'
              };
            }
          });

          if (currentEvent) {
            bookedEvents.push(currentEvent);
          }
        });

        setEvents(bookedEvents);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const getDayName = (date: Date): string => {
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    return days[date.getDay()];
  };

  const getEventsForDay = (dayIndex: number): ReservationEvent[] => {
    return events.filter(event => event.dayIndex === dayIndex);
  };

  const calculateEventPosition = (startHour: number, endHour: number) => {
    const startIndex = startHour - 9;
    const duration = endHour - startHour;
    return {
      top: startIndex * 60,
      height: duration * 60
    };
  };

  if (isLoading) {
    return (
      <Body>
        <Header>
          <BackButton onClick={handleGoBack}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </BackButton>
          예약현황
        </Header>
        <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
        <NavigationBar />
      </Body>
    );
  }

  return (
    <Body>
      <Header>
        <BackButton onClick={handleGoBack}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </BackButton>
        예약현황
      </Header>

      {facilityData && (
        <FacilityInfo>
          <FacilityImage>
            {facilityData.imageUrl && (
              <img src={facilityData.imageUrl} alt={facilityData.name} />
            )}
          </FacilityImage>
          <FacilityDetails>
            <FacilityName>{facilityData.name}</FacilityName>
            <FacilityLocation>
              {facilityData.building.name} {facilityData.location}
            </FacilityLocation>
          </FacilityDetails>
        </FacilityInfo>
      )}

      <CalendarContainer>
        <WeekHeader>
          <TimeColumn />
          {currentWeek.map((date, index) => (
            <DayHeader key={index}>
              <span className="day">{getDayName(date)}</span>
              <span className="date">{date.getDate()}</span>
            </DayHeader>
          ))}
        </WeekHeader>

        <TimeGrid>
          {/* Time column */}
          <div>
            {timeSlots.map((hour) => (
              <TimeSlot key={hour}>
                {hour}
              </TimeSlot>
            ))}
          </div>

          {/* Day columns */}
          {currentWeek.map((date, dayIndex) => (
            <DayColumn key={dayIndex}>
              {timeSlots.map((hour) => (
                <DayCell key={hour} />
              ))}

              {/* Events for this day */}
              {getEventsForDay(dayIndex).map((event, eventIndex) => {
                const { top, height } = calculateEventPosition(event.startHour, event.endHour);

                return (
                  <EventBlock key={eventIndex} top={top} height={height}>
                    {event.title}
                  </EventBlock>
                );
              })}
            </DayColumn>
          ))}
        </TimeGrid>
      </CalendarContainer>

      <NavigationBar />
    </Body>
  );
};

export default Status;
